<template>
  <div
    class="oct-text"
    :data-ab-id="dataAbId"
  >
    <div class="container text-center">
      <div class="flex gap-1">
        <span>{{ topTitle }}</span>
        <KikLegalInformationDrawer
          v-if="enableLegalDisclaimer && titleOrSubtitle === 'title'"
          :legal-text="legalText"
          :title="legalTitle"
        />
      </div>
      <div class="flex gap-1">
        <KikHeading
          :level="2"
          :title="title"
          :description="subText"
        />
        <KikLegalInformationDrawer
          v-if="enableLegalDisclaimer && titleOrSubtitle === 'subtitle'"
          :legal-text="legalText"
          :title="legalTitle"
        />
      </div>
      <div
        class="button-container"
      >
        <div
          v-for="(link, i) in linksData"
          :key="i"
          class="button-wrapper"
          :class="{
            'text-right': i % 2 === 0,
            'text-left': i % 2 !== 0,
          }"
        >
          <SharedTargetLink
            v-if="link.ctaLink"
            :url="link.ctaLink"
            :target="link.linkTargetMode"
          >
            {{ link.ctaText }}
          </SharedTargetLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import magnoliaGetters from '~/composables/getters/magnoliaGetters';

const AMOUNT_SLIDES_TO_DISPLAY = 4;

interface Props {
  backgroundColor?: string;
  topTitle?: string;
  title?: string;
  subText?: string;
  slides?: any[];
  intLinkParam?: string;
  dataAbId?: string;
  enableLegalDisclaimer?: boolean;
  legalText?: string;
  legalTitle?: string;
  titleOrSubtitle?: string;
}

const props = withDefaults(defineProps<Props>(), {
  backgroundColor: 'unset',
  topTitle: '',
  title: '',
  subText: '',
  legalText: '',
  legalTitle: '',
  titleOrSubtitle: 'title',
  enableLegalDisclaimer: false,
  slides: () => ([]),
  intLinkParam: '',
});
const slides = toRef(props, 'slides');

const linksData = computed(() => (
  slides.value?.slice(0, AMOUNT_SLIDES_TO_DISPLAY).map(slide => ({
    ...slide,
    ctaLink: magnoliaGetters.getLink({
      ...(typeof slide.pageLink === 'object' ? slide.pageLink : { '@path': slide.pageLink }),

    }, props.intLinkParam),
  }))
));
</script>

<style lang="scss" scoped>
.oct-text {
  margin: var(--spacer-base) 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgb(176, 192, 176);
  background-color: v-bind(backgroundColor);
  padding: 50px 110px;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    padding: 20px 20px;
  }
  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    padding: 50px 80px;
  }
}

.container{
  display: flex;
  padding: 30px;
  flex-direction: column;
  align-items: center;
  position: relative;
  background: var(--c-white);
  width: 100%;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    text-align: center;
    padding: 20px;
  }
}

span{
  color: rgb(219, 56, 56);
  font-size: var(--font-size--sm);
  font-weight: bold;
  letter-spacing: 1px;
  text-transform: uppercase;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    font-size: var(--font-size--xs);
    letter-spacing: 0.15px;
  }
}

h2{
  color: var(--c-dark);
  font-size: 30px;
  font-weight: bold;
  letter-spacing: 0.22px;
  line-height: 41px;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    font-size: 20px;
    letter-spacing: 0.15px;
    line-height: 27px;
  }
  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    font-size: var(--h1-font-size);
    letter-spacing: 0px;
    line-height: 33px;
  }
}

p{
  margin: var(--spacer-xs) 0 0 0;
  color: var(--c-dark);
  font-size: var(--font-size--base);
  letter-spacing: 0.5px;
  line-height: 22px;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    margin: var(--spacer-2xs) 0 0 0;
    font-size: var(--font-size--sm);
    letter-spacing: 0.25px;
    line-height: 19px;
  }
}

.button-container{
  margin-top: var(--spacer-base);
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    max-width: 100%;
    flex: 0 0 100%;
    gap: 10px;
    margin-top: var(--spacer-sm);
  }
  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    max-width: 100%;
    flex: 0 0 50%;
    gap: 20px;
    .left-align  { justify-content: left }
    .right-align { justify-content: right }
  }
}

.button-wrapper{
  display: flex;
  margin: 0 10px;

  //Mobile
  @media screen and (max-width: $mobile-max) {
    margin: 0;
    width: 100%;
  }
  //Tablet
  @media (min-width: $tablet-min) and (max-width: $tablet-max) {
    margin: 0;
    width: 48%;
    justify-content: right;
  }

  a{
    padding: 10px 25px;
    border: 1.33px solid var(--c-dark);
    box-shadow: 0px 5px 20px 0px rgba(25, 25, 25, 0.08);
    border-radius: 3px;
    background: var(--c-white);
    color: var(--c-dark);
    font-size: var(--font-size--sm);
    font-weight: bold;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-decoration: none;
    text-align: center;

    //Mobile
    @media screen and (max-width: $mobile-max) {
      width: 100%;
    }
    //Tablet
    @media (min-width: $tablet-min) and (max-width: $tablet-max) {
      width: 127px;
    }
  }
}
</style>
